import React, { useState, useEffect } from 'react';

// Constantes de dientes
const ADULT_TOP_RIGHT = [18, 17, 16, 15, 14, 13, 12, 11];
const ADULT_TOP_LEFT = [21, 22, 23, 24, 25, 26, 27, 28];
const CHILD_TOP_RIGHT = [55, 54, 53, 52, 51];
const CHILD_TOP_LEFT = [61, 62, 63, 64, 65];

const CHILD_BOTTOM_RIGHT = [85, 84, 83, 82, 81];
const CHILD_BOTTOM_LEFT = [71, 72, 73, 74, 75];
const ADULT_BOTTOM_RIGHT = [48, 47, 46, 45, 44, 43, 42, 41];
const ADULT_BOTTOM_LEFT = [31, 32, 33, 34, 35, 36, 37, 38];

const Odontograma = ({ data, onChange, isViewOnly = false, shape = 'square' }) => {
    // data format: { "18": { top: "red", bottom: "blue", center: null, left: null, right: null, status: "red-x", recession: "", mobility: "" }, ... }
    const defaultTooth = { top: null, bottom: null, left: null, right: null, center: null, status: null, crown: null, recession: '', mobility: '' };
    const [activeColor, setActiveColor] = useState('red'); // 'red', 'blue', or null (eraser)
    const [openSymbolMenu, setOpenSymbolMenu] = useState(null);
    const [activeSymbol, setActiveSymbol] = useState(null); // the selected symbol to apply on click

    const getToothData = (id) => data[id] || { ...defaultTooth };

    const updateTooth = (id, key, val) => {
        if (isViewOnly) return;
        const currentData = { ...getToothData(id) };
        currentData[key] = val;
        onChange({ ...data, [id]: currentData });
    };

    const applySymbol = (id, symbol) => {
        const currentData = getToothData(id);
        if (symbol === 'total_prosthesis' || symbol === 'blue-total_prosthesis') {
            const adultTop = [...ADULT_TOP_RIGHT, ...ADULT_TOP_LEFT];
            const adultBottom = [...ADULT_BOTTOM_RIGHT, ...ADULT_BOTTOM_LEFT];
            const childTop = [...CHILD_TOP_RIGHT, ...CHILD_TOP_LEFT];
            const childBottom = [...CHILD_BOTTOM_RIGHT, ...CHILD_BOTTOM_LEFT];
            
            const numericId = parseInt(id, 10);
            let targetGroup = [];
            
            if (adultTop.includes(numericId)) targetGroup = adultTop;
            else if (adultBottom.includes(numericId)) targetGroup = adultBottom;
            else if (childTop.includes(numericId)) targetGroup = childTop;
            else if (childBottom.includes(numericId)) targetGroup = childBottom;
            
            if (targetGroup.length > 0) {
                const newData = { ...data };
                targetGroup.forEach(toothId => {
                    newData[toothId] = { ...(newData[toothId] || defaultTooth), status: symbol, crown: null, top: null, bottom: null, left: null, right: null, center: null };
                });
                onChange(newData);
            } else {
                onChange({ ...data, [id]: { ...currentData, status: symbol, crown: null, top: null, bottom: null, left: null, right: null, center: null } });
            }
        } else if (symbol === 'crown' || symbol === 'blue-crown') {
            const crownColor = symbol === 'crown' ? 'red' : 'blue';
            let newStatus = currentData.status;
            // Solo el triángulo (endodoncia) puede coexistir con la corona
            if (newStatus !== 'endodontics' && newStatus !== 'blue-endodontics') {
                newStatus = null;
            }
            onChange({ ...data, [id]: { ...currentData, status: newStatus, crown: crownColor, top: null, bottom: null, left: null, right: null, center: null } });
        } else if (symbol === 'endodontics' || symbol === 'blue-endodontics') {
            // No borra la corona si ya existe
            onChange({ ...data, [id]: { ...currentData, status: symbol, top: null, bottom: null, left: null, right: null, center: null } });
        } else {
            onChange({ ...data, [id]: { ...currentData, status: symbol, crown: null, top: null, bottom: null, left: null, right: null, center: null } });
        }
    };

    const handleFaceClick = (id, face) => {
        if (isViewOnly) return;
        
        if (activeSymbol) {
            applySymbol(id, activeSymbol);
            return;
        }

        if (activeColor === null) {
            const current = getToothData(id);
            if (current.status) {
                updateTooth(id, 'status', null);
            } else {
                updateTooth(id, face, null);
            }
        } else {
            updateTooth(id, face, activeColor);
        }
    };

    const handleDrop = (e, id) => {
        if (isViewOnly) return;
        e.preventDefault();
        e.stopPropagation();
        const symbol = e.dataTransfer.getData('odontogram_symbol');
        if (symbol) {
            applySymbol(id, symbol);
        }
    };

    const handleDragOver = (e) => {
        if (isViewOnly) return;
        e.preventDefault();
        e.stopPropagation();
    };

    const handleToothDoubleClick = (id) => {
        if (isViewOnly) return;

        if (activeSymbol) {
            applySymbol(id, activeSymbol);
            return;
        }

        const current = getToothData(id);
        
        if (activeColor === null) {
            // Borrador: limpiar todo el diente (excepto recesión y movilidad)
            const newData = { ...data };
            newData[id] = {
                ...defaultTooth,
                recession: current.recession,
                mobility: current.mobility
            };
            onChange(newData);
            return;
        }
    };

    const renderAdultTooth = (id) => {
        const t = getToothData(id);
        const getColor = (c) => c === 'red' ? '#ff4d4d' : c === 'blue' ? '#4d4dff' : 'white';

        return (
            <div 
                key={id} 
                style={{ display: 'inline-block', width: '40px', minWidth: '40px', flexShrink: 0, textAlign: 'center', margin: '2px' }}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, id)}
            >
                <div style={{ fontSize: '10px', fontWeight: 'bold' }}>{id}</div>
                <div style={{ position: 'relative', width: '40px', height: '40px', cursor: isViewOnly ? 'default' : 'pointer' }} onDoubleClick={() => handleToothDoubleClick(id)}>
                    <svg width="40" height="40" viewBox="0 0 40 40">
                        {/* Top */}
                        {shape === 'circle' ? 
                            <path d="M 12.93,12.93 L 7.27,7.27 A 18,18 0 0,1 32.73,7.27 L 27.07,12.93 A 10,10 0 0,0 12.93,12.93 Z" fill={getColor(t.top)} stroke="black" strokeWidth="1" onClick={() => handleFaceClick(id, 'top')} /> :
                            <polygon points="0,0 40,0 30,10 10,10" fill={getColor(t.top)} stroke="black" strokeWidth="1" onClick={() => handleFaceClick(id, 'top')} />
                        }
                        {/* Right */}
                        {shape === 'circle' ? 
                            <path d="M 27.07,12.93 L 32.73,7.27 A 18,18 0 0,1 32.73,32.73 L 27.07,27.07 A 10,10 0 0,0 27.07,12.93 Z" fill={getColor(t.right)} stroke="black" strokeWidth="1" onClick={() => handleFaceClick(id, 'right')} /> :
                            <polygon points="40,0 40,40 30,30 30,10" fill={getColor(t.right)} stroke="black" strokeWidth="1" onClick={() => handleFaceClick(id, 'right')} />
                        }
                        {/* Bottom */}
                        {shape === 'circle' ? 
                            <path d="M 27.07,27.07 L 32.73,32.73 A 18,18 0 0,1 7.27,32.73 L 12.93,27.07 A 10,10 0 0,0 27.07,27.07 Z" fill={getColor(t.bottom)} stroke="black" strokeWidth="1" onClick={() => handleFaceClick(id, 'bottom')} /> :
                            <polygon points="0,40 40,40 30,30 10,30" fill={getColor(t.bottom)} stroke="black" strokeWidth="1" onClick={() => handleFaceClick(id, 'bottom')} />
                        }
                        {/* Left */}
                        {shape === 'circle' ? 
                            <path d="M 12.93,27.07 L 7.27,32.73 A 18,18 0 0,1 7.27,7.27 L 12.93,12.93 A 10,10 0 0,0 12.93,27.07 Z" fill={getColor(t.left)} stroke="black" strokeWidth="1" onClick={() => handleFaceClick(id, 'left')} /> :
                            <polygon points="0,0 0,40 10,30 10,10" fill={getColor(t.left)} stroke="black" strokeWidth="1" onClick={() => handleFaceClick(id, 'left')} />
                        }
                        {/* Center */}
                        {shape === 'circle' ? 
                            <circle cx="20" cy="20" r="10" fill={getColor(t.center)} stroke="black" strokeWidth="1" onClick={() => handleFaceClick(id, 'center')} /> :
                            <rect x="10" y="10" width="20" height="20" fill={getColor(t.center)} stroke="black" strokeWidth="1" onClick={() => handleFaceClick(id, 'center')} />
                        }
                        
                        {/* Status overlays */}
                        {t.status === 'red-x' && <path d="M0,0 L40,40 M40,0 L0,40" stroke="red" strokeWidth="3" pointerEvents="none" />}
                        {t.status === 'blue-x' && <path d="M0,0 L40,40 M40,0 L0,40" stroke="blue" strokeWidth="3" pointerEvents="none" />}
                        {t.status === 'red-circle' && <circle cx="20" cy="20" r="18" fill="none" stroke="red" strokeWidth="3" pointerEvents="none" />}
                        {t.status === 'blue-circle' && <circle cx="20" cy="20" r="18" fill="none" stroke="blue" strokeWidth="3" pointerEvents="none" />}
                        
                        {t.status === 'red-asterisk' && (
                            <g pointerEvents="none" stroke="red" strokeWidth="3">
                                <line x1="20" y1="4" x2="20" y2="36" />
                                <line x1="4" y1="20" x2="36" y2="20" />
                                <line x1="8" y1="8" x2="32" y2="32" />
                                <line x1="32" y1="8" x2="8" y2="32" />
                            </g>
                        )}
                        {t.status === 'blue-asterisk' && (
                            <g pointerEvents="none" stroke="blue" strokeWidth="3">
                                <line x1="20" y1="4" x2="20" y2="36" />
                                <line x1="4" y1="20" x2="36" y2="20" />
                                <line x1="8" y1="8" x2="32" y2="32" />
                                <line x1="32" y1="8" x2="8" y2="32" />
                            </g>
                        )}
                        
                        {/* Drag and drop symbols */}
                        {t.status === 'loss' && (
                            <g pointerEvents="none">
                                <circle cx="20" cy="20" r="18" fill="none" stroke="red" strokeWidth="2" />
                                <path d="M10,10 L30,30 M30,10 L10,30" stroke="red" strokeWidth="2" />
                            </g>
                        )}
                        {t.status === 'blue-loss' && (
                            <g pointerEvents="none">
                                <circle cx="20" cy="20" r="18" fill="none" stroke="blue" strokeWidth="2" />
                                <path d="M10,10 L30,30 M30,10 L10,30" stroke="blue" strokeWidth="2" />
                            </g>
                        )}
                        {t.status === 'endodontics' && <polygon points="20,2 38,38 2,38" fill="none" stroke="red" strokeWidth="3" pointerEvents="none" />}
                        {t.status === 'blue-endodontics' && <polygon points="20,2 38,38 2,38" fill="none" stroke="blue" strokeWidth="3" pointerEvents="none" />}
                        {t.status === 'removable_prosthesis' && (
                            <g pointerEvents="none">
                                <line x1="5" y1="20" x2="35" y2="20" stroke="red" strokeWidth="3" />
                                <text x="2" y="25" fill="red" fontSize="16" fontWeight="bold">(</text>
                                <text x="32" y="25" fill="red" fontSize="16" fontWeight="bold">)</text>
                            </g>
                        )}
                        {t.status === 'blue-removable_prosthesis' && (
                            <g pointerEvents="none">
                                <line x1="5" y1="20" x2="35" y2="20" stroke="blue" strokeWidth="3" />
                                <text x="2" y="25" fill="blue" fontSize="16" fontWeight="bold">(</text>
                                <text x="32" y="25" fill="blue" fontSize="16" fontWeight="bold">)</text>
                            </g>
                        )}
                        {t.status === 'total_prosthesis' && (
                            <g pointerEvents="none">
                                <line x1="5" y1="15" x2="35" y2="15" stroke="red" strokeWidth="4" />
                                <line x1="5" y1="25" x2="35" y2="25" stroke="red" strokeWidth="4" />
                            </g>
                        )}
                        {t.status === 'blue-total_prosthesis' && (
                            <g pointerEvents="none">
                                <line x1="5" y1="15" x2="35" y2="15" stroke="blue" strokeWidth="4" />
                                <line x1="5" y1="25" x2="35" y2="25" stroke="blue" strokeWidth="4" />
                            </g>
                        )}
                        {t.crown === 'red' && (
                            <g pointerEvents="none">
                                <rect x="2" y="2" width="36" height="36" fill="none" stroke="red" strokeWidth="2" />
                                <rect x="8" y="8" width="24" height="24" fill="none" stroke="red" strokeWidth="2" />
                                <rect x="14" y="14" width="12" height="12" fill="none" stroke="red" strokeWidth="2" />
                            </g>
                        )}
                        {t.crown === 'blue' && (
                            <g pointerEvents="none">
                                <rect x="2" y="2" width="36" height="36" fill="none" stroke="blue" strokeWidth="2" />
                                <rect x="8" y="8" width="24" height="24" fill="none" stroke="blue" strokeWidth="2" />
                                <rect x="14" y="14" width="12" height="12" fill="none" stroke="blue" strokeWidth="2" />
                            </g>
                        )}
                    </svg>
                </div>
            </div>
        );
    };

    const renderChildTooth = (id) => {
        const t = getToothData(id);
        const getColor = (c) => c === 'red' ? '#ff4d4d' : c === 'blue' ? '#4d4dff' : 'white';

        return (
            <div 
                key={id} 
                style={{ display: 'inline-block', width: '40px', minWidth: '40px', flexShrink: 0, textAlign: 'center', margin: '2px' }}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, id)}
            >
                <div style={{ fontSize: '10px', fontWeight: 'bold' }}>{id}</div>
                <div style={{ position: 'relative', width: '40px', height: '40px', cursor: isViewOnly ? 'default' : 'pointer' }} onDoubleClick={() => handleToothDoubleClick(id)}>
                    <svg width="40" height="40" viewBox="0 0 40 40">
                        {/* Center circle */}
                        <circle cx="20" cy="20" r="10" fill={getColor(t.center)} stroke="black" strokeWidth="1" onClick={() => handleFaceClick(id, 'center')} />
                        {/* Top arc */}
                        <path d="M 12.93,12.93 L 7.27,7.27 A 18,18 0 0,1 32.73,7.27 L 27.07,12.93 A 10,10 0 0,0 12.93,12.93 Z" fill={getColor(t.top)} stroke="black" strokeWidth="1" onClick={() => handleFaceClick(id, 'top')} />
                        {/* Right arc */}
                        <path d="M 27.07,12.93 L 32.73,7.27 A 18,18 0 0,1 32.73,32.73 L 27.07,27.07 A 10,10 0 0,0 27.07,12.93 Z" fill={getColor(t.right)} stroke="black" strokeWidth="1" onClick={() => handleFaceClick(id, 'right')} />
                        {/* Bottom arc */}
                        <path d="M 27.07,27.07 L 32.73,32.73 A 18,18 0 0,1 7.27,32.73 L 12.93,27.07 A 10,10 0 0,0 27.07,27.07 Z" fill={getColor(t.bottom)} stroke="black" strokeWidth="1" onClick={() => handleFaceClick(id, 'bottom')} />
                        {/* Left arc */}
                        <path d="M 12.93,27.07 L 7.27,32.73 A 18,18 0 0,1 7.27,7.27 L 12.93,12.93 A 10,10 0 0,0 12.93,27.07 Z" fill={getColor(t.left)} stroke="black" strokeWidth="1" onClick={() => handleFaceClick(id, 'left')} />
                        
                        {/* Status overlays */}
                        {t.status === 'red-x' && <path d="M2,2 L38,38 M38,2 L2,38" stroke="red" strokeWidth="3" pointerEvents="none" />}
                        {t.status === 'blue-x' && <path d="M2,2 L38,38 M38,2 L2,38" stroke="blue" strokeWidth="3" pointerEvents="none" />}
                        {t.status === 'red-circle' && <circle cx="20" cy="20" r="19" fill="none" stroke="red" strokeWidth="3" pointerEvents="none" />}
                        {t.status === 'blue-circle' && <circle cx="20" cy="20" r="19" fill="none" stroke="blue" strokeWidth="3" pointerEvents="none" />}

                        {/* Drag and drop symbols */}
                        {t.status === 'loss' && (
                            <g pointerEvents="none">
                                <circle cx="20" cy="20" r="19" fill="none" stroke="red" strokeWidth="2" />
                                <path d="M8,8 L32,32 M32,8 L8,32" stroke="red" strokeWidth="2" />
                            </g>
                        )}
                        {t.status === 'blue-loss' && (
                            <g pointerEvents="none">
                                <circle cx="20" cy="20" r="19" fill="none" stroke="blue" strokeWidth="2" />
                                <path d="M8,8 L32,32 M32,8 L8,32" stroke="blue" strokeWidth="2" />
                            </g>
                        )}
                        {t.status === 'endodontics' && <polygon points="20,2 38,38 2,38" fill="none" stroke="red" strokeWidth="3" pointerEvents="none" />}
                        {t.status === 'blue-endodontics' && <polygon points="20,2 38,38 2,38" fill="none" stroke="blue" strokeWidth="3" pointerEvents="none" />}
                        {t.status === 'removable_prosthesis' && (
                            <g pointerEvents="none">
                                <line x1="5" y1="20" x2="35" y2="20" stroke="red" strokeWidth="3" />
                                <text x="2" y="25" fill="red" fontSize="16" fontWeight="bold">(</text>
                                <text x="32" y="25" fill="red" fontSize="16" fontWeight="bold">)</text>
                            </g>
                        )}
                        {t.status === 'blue-removable_prosthesis' && (
                            <g pointerEvents="none">
                                <line x1="5" y1="20" x2="35" y2="20" stroke="blue" strokeWidth="3" />
                                <text x="2" y="25" fill="blue" fontSize="16" fontWeight="bold">(</text>
                                <text x="32" y="25" fill="blue" fontSize="16" fontWeight="bold">)</text>
                            </g>
                        )}
                        {t.status === 'total_prosthesis' && (
                            <g pointerEvents="none">
                                <line x1="5" y1="15" x2="35" y2="15" stroke="red" strokeWidth="4" />
                                <line x1="5" y1="25" x2="35" y2="25" stroke="red" strokeWidth="4" />
                            </g>
                        )}
                        {t.status === 'blue-total_prosthesis' && (
                            <g pointerEvents="none">
                                <line x1="5" y1="15" x2="35" y2="15" stroke="blue" strokeWidth="4" />
                                <line x1="5" y1="25" x2="35" y2="25" stroke="blue" strokeWidth="4" />
                            </g>
                        )}
                        {t.crown === 'red' && (
                            <g pointerEvents="none">
                                <rect x="2" y="2" width="36" height="36" fill="none" stroke="red" strokeWidth="2" />
                                <rect x="8" y="8" width="24" height="24" fill="none" stroke="red" strokeWidth="2" />
                                <rect x="14" y="14" width="12" height="12" fill="none" stroke="red" strokeWidth="2" />
                            </g>
                        )}
                        {t.crown === 'blue' && (
                            <g pointerEvents="none">
                                <rect x="2" y="2" width="36" height="36" fill="none" stroke="blue" strokeWidth="2" />
                                <rect x="8" y="8" width="24" height="24" fill="none" stroke="blue" strokeWidth="2" />
                                <rect x="14" y="14" width="12" height="12" fill="none" stroke="blue" strokeWidth="2" />
                            </g>
                        )}
                    </svg>
                </div>
            </div>
        );
    };

    const renderBoxesRow = (ids, type) => {
        return (
            <div style={{ display: 'flex', gap: '4px' }}>
                {ids.map(id => (
                    <div key={`${type}-${id}`} style={{ width: '40px', textAlign: 'center' }}>
                        <input
                            type="text"
                            maxLength="3"
                            style={{ width: '38px', height: '18px', fontSize: '10px', textAlign: 'center', border: '1px solid black', padding: 0 }}
                            value={getToothData(id)[type] || ''}
                            onChange={(e) => updateTooth(id, type, e.target.value)}
                            disabled={isViewOnly}
                        />
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div id="odontogram-container" style={{ width: '100%', fontFamily: 'Calibri, sans-serif' }}>
            {/* HERRAMIENTAS */}
            {!isViewOnly && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '5px', border: '1px solid #ddd', alignItems: 'center' }} className="d-print-none">
                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 'bold', alignSelf: 'center', marginRight: '10px', width: '90px' }}>Herramienta:</span>
                        <button 
                            type="button"
                            onClick={() => { setActiveColor('red'); setActiveSymbol(null); }} 
                            style={{ padding: '5px 15px', backgroundColor: activeColor === 'red' ? '#ff4d4d' : '#fff', color: activeColor === 'red' ? '#fff' : '#000', border: '2px solid #ff4d4d', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                        >
                             Patología (Rojo)
                        </button>
                        <button 
                            type="button"
                            onClick={() => { setActiveColor('blue'); setActiveSymbol(null); }} 
                            style={{ padding: '5px 15px', backgroundColor: activeColor === 'blue' ? '#4d4dff' : '#fff', color: activeColor === 'blue' ? '#fff' : '#000', border: '2px solid #4d4dff', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                        >
                             Tratamiento (Azul)
                        </button>
                        <button 
                            type="button"
                            onClick={() => { setActiveColor(null); setActiveSymbol(null); }} 
                            style={{ padding: '5px 15px', backgroundColor: activeColor === null && activeSymbol === null ? '#ddd' : '#fff', color: '#000', border: '2px solid #999', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                        >
                             Borrador
                        </button>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 'bold', marginRight: '10px' }}>Símbolos:</span>
                        {/* Pérdida */}
                        <div style={{ position: 'relative' }}>
                            <div onClick={() => setOpenSymbolMenu(openSymbolMenu === 'loss' ? null : 'loss')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', cursor: 'pointer', padding: '5px', border: (openSymbolMenu === 'loss' || activeSymbol === 'loss' || activeSymbol === 'blue-loss') ? '1px solid #333' : '1px solid transparent', borderRadius: '4px', backgroundColor: (openSymbolMenu === 'loss' || activeSymbol === 'loss' || activeSymbol === 'blue-loss') ? '#eee' : 'transparent' }} title="Pérdida / Ausente">
                                <span style={{ color: '#333', border: '1px solid #333', borderRadius: '50%', width: '16px', height: '16px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', paddingBottom: '1px' }}>X</span>
                            </div>
                            {openSymbolMenu === 'loss' && (
                                <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px', padding: '5px', zIndex: 10, display: 'flex', flexDirection: 'column', gap: '5px', boxShadow: '0 2px 5px rgba(0,0,0,0.2)' }}>
                                    <div onClick={(e) => { e.stopPropagation(); setActiveSymbol('loss'); setActiveColor(null); setOpenSymbolMenu(null); }} draggable onDragStart={(e) => { e.dataTransfer.setData('odontogram_symbol', 'loss'); setOpenSymbolMenu(null); }} style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', padding: '5px', backgroundColor: activeSymbol === 'loss' ? '#f0f0f0' : 'transparent' }} title="Pérdida (Roja)">
                                        <span style={{ color: 'red', border: '1px solid red', borderRadius: '50%', width: '16px', height: '16px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', paddingBottom: '1px' }}>X</span> <span style={{fontSize:'10px'}}>Rojo</span>
                                    </div>
                                    <div onClick={(e) => { e.stopPropagation(); setActiveSymbol('blue-loss'); setActiveColor(null); setOpenSymbolMenu(null); }} draggable onDragStart={(e) => { e.dataTransfer.setData('odontogram_symbol', 'blue-loss'); setOpenSymbolMenu(null); }} style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', padding: '5px', backgroundColor: activeSymbol === 'blue-loss' ? '#f0f0f0' : 'transparent' }} title="Pérdida (Azul)">
                                        <span style={{ color: 'blue', border: '1px solid blue', borderRadius: '50%', width: '16px', height: '16px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', paddingBottom: '1px' }}>X</span> <span style={{fontSize:'10px'}}>Azul</span>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        {/* Endodoncia */}
                        <div style={{ position: 'relative' }}>
                            <div onClick={() => setOpenSymbolMenu(openSymbolMenu === 'endodontics' ? null : 'endodontics')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', cursor: 'pointer', padding: '5px', border: (openSymbolMenu === 'endodontics' || activeSymbol === 'endodontics' || activeSymbol === 'blue-endodontics') ? '1px solid #333' : '1px solid transparent', borderRadius: '4px', backgroundColor: (openSymbolMenu === 'endodontics' || activeSymbol === 'endodontics' || activeSymbol === 'blue-endodontics') ? '#eee' : 'transparent' }} title="Endodoncia">
                                <span style={{ color: '#333', fontWeight: 'bold', border: '1px solid #333', padding: '0 2px', fontSize: '12px' }}>&Delta;</span>
                            </div>
                            {openSymbolMenu === 'endodontics' && (
                                <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px', padding: '5px', zIndex: 10, display: 'flex', flexDirection: 'column', gap: '5px', boxShadow: '0 2px 5px rgba(0,0,0,0.2)' }}>
                                    <div onClick={(e) => { e.stopPropagation(); setActiveSymbol('endodontics'); setActiveColor(null); setOpenSymbolMenu(null); }} draggable onDragStart={(e) => { e.dataTransfer.setData('odontogram_symbol', 'endodontics'); setOpenSymbolMenu(null); }} style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', padding: '5px', backgroundColor: activeSymbol === 'endodontics' ? '#f0f0f0' : 'transparent' }} title="Endodoncia (Roja)">
                                        <span style={{ color: 'red', fontWeight: 'bold', border: '1px solid red', padding: '0 2px', fontSize: '12px' }}>&Delta;</span> <span style={{fontSize:'10px'}}>Rojo</span>
                                    </div>
                                    <div onClick={(e) => { e.stopPropagation(); setActiveSymbol('blue-endodontics'); setActiveColor(null); setOpenSymbolMenu(null); }} draggable onDragStart={(e) => { e.dataTransfer.setData('odontogram_symbol', 'blue-endodontics'); setOpenSymbolMenu(null); }} style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', padding: '5px', backgroundColor: activeSymbol === 'blue-endodontics' ? '#f0f0f0' : 'transparent' }} title="Endodoncia (Azul)">
                                        <span style={{ color: 'blue', fontWeight: 'bold', border: '1px solid blue', padding: '0 2px', fontSize: '12px' }}>&Delta;</span> <span style={{fontSize:'10px'}}>Azul</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Prótesis Removible */}
                        <div style={{ position: 'relative' }}>
                            <div onClick={() => setOpenSymbolMenu(openSymbolMenu === 'removable' ? null : 'removable')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', cursor: 'pointer', padding: '5px', border: (openSymbolMenu === 'removable' || activeSymbol === 'removable_prosthesis' || activeSymbol === 'blue-removable_prosthesis') ? '1px solid #333' : '1px solid transparent', borderRadius: '4px', backgroundColor: (openSymbolMenu === 'removable' || activeSymbol === 'removable_prosthesis' || activeSymbol === 'blue-removable_prosthesis') ? '#eee' : 'transparent' }} title="Prótesis Removible">
                                <span style={{ color: '#333', fontWeight: 'bold', fontSize: '14px' }}>(&mdash;)</span>
                            </div>
                            {openSymbolMenu === 'removable' && (
                                <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px', padding: '5px', zIndex: 10, display: 'flex', flexDirection: 'column', gap: '5px', boxShadow: '0 2px 5px rgba(0,0,0,0.2)' }}>
                                    <div onClick={(e) => { e.stopPropagation(); setActiveSymbol('removable_prosthesis'); setActiveColor(null); setOpenSymbolMenu(null); }} draggable onDragStart={(e) => { e.dataTransfer.setData('odontogram_symbol', 'removable_prosthesis'); setOpenSymbolMenu(null); }} style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', padding: '5px', backgroundColor: activeSymbol === 'removable_prosthesis' ? '#f0f0f0' : 'transparent' }} title="Prótesis Removible (Roja)">
                                        <span style={{ color: 'red', fontWeight: 'bold', fontSize: '14px' }}>(&mdash;)</span> <span style={{fontSize:'10px'}}>Rojo</span>
                                    </div>
                                    <div onClick={(e) => { e.stopPropagation(); setActiveSymbol('blue-removable_prosthesis'); setActiveColor(null); setOpenSymbolMenu(null); }} draggable onDragStart={(e) => { e.dataTransfer.setData('odontogram_symbol', 'blue-removable_prosthesis'); setOpenSymbolMenu(null); }} style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', padding: '5px', backgroundColor: activeSymbol === 'blue-removable_prosthesis' ? '#f0f0f0' : 'transparent' }} title="Prótesis Removible (Azul)">
                                        <span style={{ color: 'blue', fontWeight: 'bold', fontSize: '14px' }}>(&mdash;)</span> <span style={{fontSize:'10px'}}>Azul</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Prótesis Total */}
                        <div style={{ position: 'relative' }}>
                            <div onClick={() => setOpenSymbolMenu(openSymbolMenu === 'total' ? null : 'total')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', cursor: 'pointer', padding: '5px', border: (openSymbolMenu === 'total' || activeSymbol === 'total_prosthesis' || activeSymbol === 'blue-total_prosthesis') ? '1px solid #333' : '1px solid transparent', borderRadius: '4px', backgroundColor: (openSymbolMenu === 'total' || activeSymbol === 'total_prosthesis' || activeSymbol === 'blue-total_prosthesis') ? '#eee' : 'transparent' }} title="Prótesis Total">
                                <span style={{ color: '#333', fontWeight: 'bold', fontSize: '16px' }}>=</span>
                            </div>
                            {openSymbolMenu === 'total' && (
                                <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px', padding: '5px', zIndex: 10, display: 'flex', flexDirection: 'column', gap: '5px', boxShadow: '0 2px 5px rgba(0,0,0,0.2)' }}>
                                    <div onClick={(e) => { e.stopPropagation(); setActiveSymbol('total_prosthesis'); setActiveColor(null); setOpenSymbolMenu(null); }} draggable onDragStart={(e) => { e.dataTransfer.setData('odontogram_symbol', 'total_prosthesis'); setOpenSymbolMenu(null); }} style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', padding: '5px', backgroundColor: activeSymbol === 'total_prosthesis' ? '#f0f0f0' : 'transparent' }} title="Prótesis Total (Roja)">
                                        <span style={{ color: 'red', fontWeight: 'bold', fontSize: '16px' }}>=</span> <span style={{fontSize:'10px'}}>Rojo</span>
                                    </div>
                                    <div onClick={(e) => { e.stopPropagation(); setActiveSymbol('blue-total_prosthesis'); setActiveColor(null); setOpenSymbolMenu(null); }} draggable onDragStart={(e) => { e.dataTransfer.setData('odontogram_symbol', 'blue-total_prosthesis'); setOpenSymbolMenu(null); }} style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', padding: '5px', backgroundColor: activeSymbol === 'blue-total_prosthesis' ? '#f0f0f0' : 'transparent' }} title="Prótesis Total (Azul)">
                                        <span style={{ color: 'blue', fontWeight: 'bold', fontSize: '16px' }}>=</span> <span style={{fontSize:'10px'}}>Azul</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Corona */}
                        <div style={{ position: 'relative' }}>
                            <div onClick={() => setOpenSymbolMenu(openSymbolMenu === 'crown' ? null : 'crown')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', cursor: 'pointer', padding: '5px', border: (openSymbolMenu === 'crown' || activeSymbol === 'crown' || activeSymbol === 'blue-crown') ? '1px solid #333' : '1px solid transparent', borderRadius: '4px', backgroundColor: (openSymbolMenu === 'crown' || activeSymbol === 'crown' || activeSymbol === 'blue-crown') ? '#eee' : 'transparent' }} title="Corona">
                                <svg width="14" height="14" viewBox="0 0 14 14">
                                    <rect x="0" y="0" width="14" height="14" fill="none" stroke="#333" strokeWidth="1" />
                                    <rect x="3" y="3" width="8" height="8" fill="none" stroke="#333" strokeWidth="1" />
                                    <rect x="6" y="6" width="2" height="2" fill="none" stroke="#333" strokeWidth="1" />
                                </svg>
                            </div>
                            {openSymbolMenu === 'crown' && (
                                <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px', padding: '5px', zIndex: 10, display: 'flex', flexDirection: 'column', gap: '5px', boxShadow: '0 2px 5px rgba(0,0,0,0.2)' }}>
                                    <div onClick={(e) => { e.stopPropagation(); setActiveSymbol('crown'); setActiveColor(null); setOpenSymbolMenu(null); }} draggable onDragStart={(e) => { e.dataTransfer.setData('odontogram_symbol', 'crown'); setOpenSymbolMenu(null); }} style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', padding: '5px', backgroundColor: activeSymbol === 'crown' ? '#f0f0f0' : 'transparent' }} title="Corona (Roja)">
                                        <svg width="14" height="14" viewBox="0 0 14 14">
                                            <rect x="0" y="0" width="14" height="14" fill="none" stroke="red" strokeWidth="1" />
                                            <rect x="3" y="3" width="8" height="8" fill="none" stroke="red" strokeWidth="1" />
                                            <rect x="6" y="6" width="2" height="2" fill="none" stroke="red" strokeWidth="1" />
                                        </svg> <span style={{fontSize:'10px'}}>Rojo</span>
                                    </div>
                                    <div onClick={(e) => { e.stopPropagation(); setActiveSymbol('blue-crown'); setActiveColor(null); setOpenSymbolMenu(null); }} draggable onDragStart={(e) => { e.dataTransfer.setData('odontogram_symbol', 'blue-crown'); setOpenSymbolMenu(null); }} style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', padding: '5px', backgroundColor: activeSymbol === 'blue-crown' ? '#f0f0f0' : 'transparent' }} title="Corona (Azul)">
                                        <svg width="14" height="14" viewBox="0 0 14 14">
                                            <rect x="0" y="0" width="14" height="14" fill="none" stroke="blue" strokeWidth="1" />
                                            <rect x="3" y="3" width="8" height="8" fill="none" stroke="blue" strokeWidth="1" />
                                            <rect x="6" y="6" width="2" height="2" fill="none" stroke="blue" strokeWidth="1" />
                                        </svg> <span style={{fontSize:'10px'}}>Azul</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* X Grande */}
                        <div style={{ position: 'relative' }}>
                            <div onClick={() => setOpenSymbolMenu(openSymbolMenu === 'x' ? null : 'x')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', cursor: 'pointer', padding: '5px', border: (openSymbolMenu === 'x' || activeSymbol === 'red-x' || activeSymbol === 'blue-x') ? '1px solid #333' : '1px solid transparent', borderRadius: '4px', backgroundColor: (openSymbolMenu === 'x' || activeSymbol === 'red-x' || activeSymbol === 'blue-x') ? '#eee' : 'transparent' }} title="Extracción (X)">
                                <span style={{ color: '#333', fontWeight: 'bold', fontSize: '16px' }}>X</span>
                            </div>
                            {openSymbolMenu === 'x' && (
                                <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px', padding: '5px', zIndex: 10, display: 'flex', flexDirection: 'column', gap: '5px', boxShadow: '0 2px 5px rgba(0,0,0,0.2)' }}>
                                    <div onClick={(e) => { e.stopPropagation(); setActiveSymbol('red-x'); setActiveColor(null); setOpenSymbolMenu(null); }} draggable onDragStart={(e) => { e.dataTransfer.setData('odontogram_symbol', 'red-x'); setOpenSymbolMenu(null); }} style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', padding: '5px', backgroundColor: activeSymbol === 'red-x' ? '#f0f0f0' : 'transparent' }} title="X Roja">
                                        <span style={{ color: 'red', fontWeight: 'bold', fontSize: '16px' }}>X</span> <span style={{fontSize:'10px'}}>Rojo</span>
                                    </div>
                                    <div onClick={(e) => { e.stopPropagation(); setActiveSymbol('blue-x'); setActiveColor(null); setOpenSymbolMenu(null); }} draggable onDragStart={(e) => { e.dataTransfer.setData('odontogram_symbol', 'blue-x'); setOpenSymbolMenu(null); }} style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', padding: '5px', backgroundColor: activeSymbol === 'blue-x' ? '#f0f0f0' : 'transparent' }} title="X Azul">
                                        <span style={{ color: 'blue', fontWeight: 'bold', fontSize: '16px' }}>X</span> <span style={{fontSize:'10px'}}>Azul</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Asterisco */}
                        <div style={{ position: 'relative' }}>
                            <div onClick={() => setOpenSymbolMenu(openSymbolMenu === 'asterisk' ? null : 'asterisk')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', cursor: 'pointer', padding: '5px', border: (openSymbolMenu === 'asterisk' || activeSymbol === 'red-asterisk' || activeSymbol === 'blue-asterisk') ? '1px solid #333' : '1px solid transparent', borderRadius: '4px', backgroundColor: (openSymbolMenu === 'asterisk' || activeSymbol === 'red-asterisk' || activeSymbol === 'blue-asterisk') ? '#eee' : 'transparent' }} title="Asterisco">
                                <span style={{ color: '#333', fontWeight: 'bold', fontSize: '18px', lineHeight: '14px', marginTop: '6px' }}>*</span>
                            </div>
                            {openSymbolMenu === 'asterisk' && (
                                <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px', padding: '5px', zIndex: 10, display: 'flex', flexDirection: 'column', gap: '5px', boxShadow: '0 2px 5px rgba(0,0,0,0.2)' }}>
                                    <div onClick={(e) => { e.stopPropagation(); setActiveSymbol('red-asterisk'); setActiveColor(null); setOpenSymbolMenu(null); }} draggable onDragStart={(e) => { e.dataTransfer.setData('odontogram_symbol', 'red-asterisk'); setOpenSymbolMenu(null); }} style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', padding: '5px', backgroundColor: activeSymbol === 'red-asterisk' ? '#f0f0f0' : 'transparent' }} title="Asterisco Rojo">
                                        <span style={{ color: 'red', fontWeight: 'bold', fontSize: '18px', lineHeight: '14px', marginTop: '6px' }}>*</span> <span style={{fontSize:'10px'}}>Rojo</span>
                                    </div>
                                    <div onClick={(e) => { e.stopPropagation(); setActiveSymbol('blue-asterisk'); setActiveColor(null); setOpenSymbolMenu(null); }} draggable onDragStart={(e) => { e.dataTransfer.setData('odontogram_symbol', 'blue-asterisk'); setOpenSymbolMenu(null); }} style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', padding: '5px', backgroundColor: activeSymbol === 'blue-asterisk' ? '#f0f0f0' : 'transparent' }} title="Asterisco Azul">
                                        <span style={{ color: 'blue', fontWeight: 'bold', fontSize: '18px', lineHeight: '14px', marginTop: '6px' }}>*</span> <span style={{fontSize:'10px'}}>Azul</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Adult Top Row */}
            <div style={{ display: 'flex', marginBottom: '10px' }}>
                <div style={{ width: '80px', fontSize: '9px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', paddingTop: '5px' }}>
                    {shape !== 'circle' && (
                        <>
                            <div style={{ height: '18px', marginBottom: '4px' }}>RECESIÓN</div>
                            <div style={{ height: '18px', marginBottom: '4px' }}>MOVILIDAD</div>
                        </>
                    )}
                    <div style={{ height: '40px', marginTop: shape === 'circle' ? '0px' : '10px' }}>VESTIBULAR</div>
                </div>
                <div style={{ display: 'flex', gap: '20px' }}>
                    {/* Top Right Quadrant (18-11) */}
                    <div>
                        {shape !== 'circle' && (
                            <>
                                {renderBoxesRow(ADULT_TOP_RIGHT, 'recession')}
                                <div style={{ height: '4px' }}></div>
                                {renderBoxesRow(ADULT_TOP_RIGHT, 'mobility')}
                            </>
                        )}
                        <div style={{ display: 'flex', gap: '4px', marginTop: '5px' }}>
                            {ADULT_TOP_RIGHT.map(id => renderAdultTooth(id))}
                        </div>
                    </div>
                    {/* Top Left Quadrant (21-28) */}
                    <div>
                        {shape !== 'circle' && (
                            <>
                                {renderBoxesRow(ADULT_TOP_LEFT, 'recession')}
                                <div style={{ height: '4px' }}></div>
                                {renderBoxesRow(ADULT_TOP_LEFT, 'mobility')}
                            </>
                        )}
                        <div style={{ display: 'flex', gap: '4px', marginTop: '5px' }}>
                            {ADULT_TOP_LEFT.map(id => renderAdultTooth(id))}
                        </div>
                    </div>
                </div>
                <div style={{ width: '80px' }}></div>
            </div>

            {/* Child Top Row */}
            <div style={{ display: 'flex', marginBottom: '5px' }}>
                <div style={{ width: '80px', fontSize: '9px' }}></div>
                <div style={{ display: 'flex', gap: '20px', marginLeft: '132px' /* align with adult 15-11 */ }}>
                    {/* Child Top Right Quadrant (55-51) */}
                    <div style={{ display: 'flex', gap: '4px' }}>
                        {CHILD_TOP_RIGHT.map(id => renderChildTooth(id))}
                    </div>
                    {/* Child Top Left Quadrant (61-65) */}
                    <div style={{ display: 'flex', gap: '4px' }}>
                        {CHILD_TOP_LEFT.map(id => renderChildTooth(id))}
                    </div>
                </div>
                <div style={{ width: '80px' }}></div>
            </div>
            
            {/* Divider Lingual */}
            <div style={{ display: 'flex', marginBottom: '5px', alignItems: 'center' }}>
                <div style={{ width: '80px', fontSize: '9px' }}>LINGUAL</div>
                <div style={{ borderBottom: '1px solid #ccc', flex: 1, margin: '0 20px' }}></div>
            </div>

            {/* Child Bottom Row */}
            <div style={{ display: 'flex', marginBottom: '10px' }}>
                <div style={{ width: '80px', fontSize: '9px' }}></div>
                <div style={{ display: 'flex', gap: '20px', marginLeft: '132px' }}>
                    {/* Child Bottom Right Quadrant (85-81) */}
                    <div style={{ display: 'flex', gap: '4px' }}>
                        {CHILD_BOTTOM_RIGHT.map(id => renderChildTooth(id))}
                    </div>
                    {/* Child Bottom Left Quadrant (71-75) */}
                    <div style={{ display: 'flex', gap: '4px' }}>
                        {CHILD_BOTTOM_LEFT.map(id => renderChildTooth(id))}
                    </div>
                </div>
                <div style={{ width: '80px' }}></div>
            </div>

            {/* Adult Bottom Row */}
            <div style={{ display: 'flex', marginBottom: '5px' }}>
                <div style={{ width: '80px', fontSize: '9px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', paddingTop: '5px' }}>
                    <div style={{ height: '40px', marginBottom: '5px' }}>VESTIBULAR</div>
                    {shape !== 'circle' && (
                        <>
                            <div style={{ height: '18px', marginBottom: '4px' }}>MOVILIDAD</div>
                            <div style={{ height: '18px' }}>RECESIÓN</div>
                        </>
                    )}
                </div>
                <div style={{ display: 'flex', gap: '20px' }}>
                    {/* Bottom Right Quadrant (48-41) */}
                    <div>
                        <div style={{ display: 'flex', gap: '4px', marginBottom: '5px' }}>
                            {ADULT_BOTTOM_RIGHT.map(id => renderAdultTooth(id))}
                        </div>
                        {shape !== 'circle' && (
                            <>
                                {renderBoxesRow(ADULT_BOTTOM_RIGHT, 'mobility')}
                                <div style={{ height: '4px' }}></div>
                                {renderBoxesRow(ADULT_BOTTOM_RIGHT, 'recession')}
                            </>
                        )}
                    </div>
                    {/* Bottom Left Quadrant (31-38) */}
                    <div>
                        <div style={{ display: 'flex', gap: '4px', marginBottom: '5px' }}>
                            {ADULT_BOTTOM_LEFT.map(id => renderAdultTooth(id))}
                        </div>
                        {shape !== 'circle' && (
                            <>
                                {renderBoxesRow(ADULT_BOTTOM_LEFT, 'mobility')}
                                <div style={{ height: '4px' }}></div>
                                {renderBoxesRow(ADULT_BOTTOM_LEFT, 'recession')}
                            </>
                        )}
                    </div>
                </div>
                <div style={{ width: '80px' }}></div>
            </div>

            {/* Instrucciones */}
            <div style={{ fontSize: '10px', color: '#666', marginTop: '10px', fontStyle: 'italic' }}>
                * Selecciona una herramienta arriba y haz clic sobre una cara para pintarla.<br/>
                * Doble clic sobre un diente: Marcar con X o Círculo (dependiendo del color seleccionado).
            </div>
        </div>
    );
};

export default Odontograma;

